import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaPlay, FaPlus, FaSave, FaEdit } from 'react-icons/fa';
import AppToast from '../../AppToast';
import {
  createCustomScriptStudio,
  getCustomScriptStudioById,
  listCustomScriptsStudio,
  previewCustomScript,
  updateCustomScriptStudio,
} from '../../../api/CustomScriptApi';

const DEFAULT_SOURCE = `async function main(params) {
  return { success: true, message: 'OK' };
}`;

const SCRIPT_TYPES = [
  'backend',
  'snippet',
  'view_page',
  'validation',
  'workflow_script',
];

function emptyForm() {
  return {
    id: '',
    script_key: '',
    display_label: '',
    description: '',
    script_type: 'backend',
    language: 'javascript',
    source_code: DEFAULT_SOURCE,
    configText: '{}',
    ui_codeText: '',
    target_object: '',
    is_active: true,
    sort_order: '0',
  };
}

function isAdminUser(userInfo) {
  const r = String(userInfo?.role_name || userInfo?.userrole || '').toUpperCase();
  return r === 'ADMIN';
}

/** @param {{ userInfo?: object }} props */
export default function CustomScriptStudio({ userInfo }) {
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewMode, setPreviewMode] = useState('api');
  const [previewRecordId, setPreviewRecordId] = useState('');
  const [previewObjectName, setPreviewObjectName] = useState('lead');
  const [previewInputDataText, setPreviewInputDataText] = useState('{}');
  const [validationRecordText, setValidationRecordText] = useState('{}');
  const [validationPrevText, setValidationPrevText] = useState('');
  const [validationTriggerText, setValidationTriggerText] = useState('on_save');

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await listCustomScriptsStudio();
      if (res?.success && Array.isArray(res.data)) {
        setRows(res.data);
      } else {
        setRows([]);
        showToast(res?.message || 'Could not load script list', 'error');
      }
    } catch (e) {
      showToast(e?.message || String(e), 'error');
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm());
    setPreviewResult(null);
    setLoadingScript(false);
  };

  const openNewModal = () => {
    setForm(emptyForm());
    setPreviewResult(null);
    setShowModal(true);
  };

  const openEditModal = async (id) => {
    if (!id) return;
    setLoadingScript(true);
    setPreviewResult(null);
    try {
      const res = await getCustomScriptStudioById(id);
      if (!res?.success || !res.data) {
        showToast(`Load failed: ${res?.message || 'Failed to load script'}`, 'error');
        return;
      }
      const d = res.data;
      setForm({
        id: d.id,
        script_key: d.script_key || '',
        display_label: d.display_label || '',
        description: d.description || '',
        script_type: d.script_type || 'backend',
        language: d.language || 'javascript',
        source_code: d.source_code != null ? String(d.source_code) : '',
        configText:
          d.config && typeof d.config === 'object'
            ? JSON.stringify(d.config, null, 2)
            : '{}',
        ui_codeText:
          d.ui_code && typeof d.ui_code === 'object'
            ? JSON.stringify(d.ui_code, null, 2)
            : '',
        target_object: d.target_object || '',
        is_active: d.is_active !== false,
        sort_order: String(d.sort_order ?? 0),
      });
      setShowModal(true);
    } catch (e) {
      showToast(`Load failed: ${e?.message || String(e)}`, 'error');
    } finally {
      setLoadingScript(false);
    }
  };

  const onEditRow = (e, id) => {
    e.stopPropagation();
    openEditModal(id);
  };

  const parseJsonField = (text, label) => {
    const t = String(text ?? '').trim();
    if (!t) return null;
    try {
      const o = JSON.parse(t);
      if (typeof o !== 'object' || Array.isArray(o)) {
        throw new Error(`${label} must be a JSON object`);
      }
      return o;
    } catch (e) {
      throw new Error(`${label}: ${e.message || 'invalid JSON'}`);
    }
  };

  const buildPayload = () => {
    const config = parseJsonField(form.configText, 'config');
    let ui_code = null;
    const uiRaw = String(form.ui_codeText ?? '').trim();
    if (uiRaw) {
      ui_code = parseJsonField(form.ui_codeText, 'ui_code');
    }
    return {
      script_key: String(form.script_key || '').trim(),
      display_label: form.display_label?.trim() || null,
      description: form.description?.trim() || null,
      script_type: form.script_type,
      language: form.language || 'javascript',
      source_code: form.source_code ?? '',
      config,
      ui_code,
      target_object: form.target_object?.trim() || null,
      is_active: !!form.is_active,
      sort_order: parseInt(String(form.sort_order || '0'), 10) || 0,
    };
  };

  const onSave = async () => {
    if (!isAdminUser(userInfo)) {
      showToast('Administrator role required to save scripts.', 'error');
      return;
    }
    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      showToast(e.message, 'error');
      return;
    }
    if (!payload.script_key) {
      showToast('script_key is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      const editingId = form.id ? String(form.id).trim() : '';
      const res = editingId
        ? await updateCustomScriptStudio(editingId, payload)
        : await createCustomScriptStudio(payload);
      if (res?.success && res.data) {
        showToast(editingId ? 'Script updated.' : 'Script created.', 'success');
        const saved = res.data;
        setForm((prev) => ({
          ...prev,
          id: saved.id,
          script_key: saved.script_key || prev.script_key,
          sort_order: String(saved.sort_order ?? prev.sort_order),
        }));
        await loadList();
      } else {
        showToast(res?.message || 'Save failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.message || e.message || String(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    if (!isAdminUser(userInfo)) {
      showToast('Administrator role required to preview.', 'error');
      return;
    }
    const objectName = previewObjectName.trim();
    if (!objectName) {
      showToast('Preview object name is required.', 'error');
      return;
    }
    const src = String(form.source_code ?? '').trim();
    if (!src) {
      showToast('source_code is empty.', 'error');
      return;
    }
    setPreviewing(true);
    setPreviewResult(null);
    try {
      let body = {
        sourceCode: form.source_code,
        objectName,
        previewMode,
        scriptKey: form.script_key?.trim() || 'preview',
      };
      if (previewMode === 'api') {
        let inputData = {};
        try {
          inputData = parseJsonField(previewInputDataText, 'Preview inputData');
        } catch (e) {
          showToast(e.message, 'error');
          setPreviewing(false);
          return;
        }
        const rid = previewRecordId.trim();
        body = {
          ...body,
          recordId: rid || undefined,
          inputData,
        };
      } else {
        let validationRecord = {};
        let validationPreviousRecord = null;
        try {
          validationRecord = parseJsonField(validationRecordText, 'validation record');
          const pv = String(validationPrevText ?? '').trim();
          if (pv) {
            validationPreviousRecord = parseJsonField(validationPrevText, 'previousRecord');
          }
        } catch (e) {
          showToast(e.message, 'error');
          setPreviewing(false);
          return;
        }
        body = {
          ...body,
          validationTriggerKey: validationTriggerText.trim() || 'on_save',
          validationRecord,
          validationPreviousRecord,
        };
      }
      const res = await previewCustomScript(body);
      if (res?.success) {
        setPreviewResult(res.data);
      } else {
        showToast(res?.message || 'Preview failed', 'error');
      }
    } catch (e) {
      showToast(e?.response?.data?.message || e.message || String(e), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const admin = isAdminUser(userInfo);

  return (
    <div className='wrapper'>
      <div id='content' className='p-4'>
        <h4 className='mb-3'>Custom Script Studio</h4>
        <p className='text-muted small'>
          Manage <code>custom_script</code> definitions here. Open <strong>New script</strong> or <strong>Edit</strong>{' '}
          to author code in a modal (
          <code>async function main(params)</code>
          ). Administrator role required for Preview and Save.
        </p>

        {!admin && (
          <Alert variant='warning'>
            Your session does not show an Administrator role. Preview and Save will return 403 unless you are logged in as Admin.
          </Alert>
        )}

        <Card className='shadow-sm'>
          <Card.Header className='py-2 d-flex flex-wrap justify-content-between align-items-center gap-2'>
            <span className='fw-semibold'>Custom scripts</span>
            <div className='d-flex flex-wrap gap-2'>
              <Button variant='primary' size='sm' onClick={openNewModal}>
                <FaPlus className='me-1' /> New script
              </Button>
              <Button variant='outline-secondary' size='sm' onClick={loadList} disabled={loadingList}>
                Refresh list
              </Button>
            </div>
          </Card.Header>
          <Card.Body className='p-0'>
            {loadingList ? (
              <div className='p-4 text-center'>
                <Spinner animation='border' size='sm' className='me-2' />
                Loading scripts…
              </div>
            ) : rows.length === 0 ? (
              <div className='p-4 text-muted small'>
                No custom scripts yet. Click <strong>New script</strong> to create one.
              </div>
            ) : (
              <div style={{ maxHeight: 'min(520px, 70vh)', overflow: 'auto' }}>
                <Table hover responsive size='sm' className='mb-0 align-middle'>
                  <thead className='border-bottom bg-light' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ minWidth: '140px' }}>script_key</th>
                      <th>Label</th>
                      <th style={{ width: '110px' }}>Type</th>
                      <th style={{ width: '120px' }}>Object</th>
                      <th style={{ width: '88px' }}>Status</th>
                      <th style={{ width: '96px' }} className='text-end'>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const selected = showModal && form.id && String(form.id) === String(r.id);
                      return (
                        <tr
                          key={r.id}
                          role='button'
                          tabIndex={0}
                          className={selected ? 'table-active' : ''}
                          onClick={() => openEditModal(r.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEditModal(r.id);
                            }
                          }}
                        >
                          <td className='font-monospace small'>{r.script_key || '—'}</td>
                          <td>{r.display_label || <span className='text-muted'>—</span>}</td>
                          <td className='small text-muted'>{r.script_type || '—'}</td>
                          <td className='small'>{r.target_object || <span className='text-muted'>—</span>}</td>
                          <td>
                            {r.is_active === false ? (
                              <Badge bg='secondary'>Inactive</Badge>
                            ) : (
                              <Badge bg='success'>Active</Badge>
                            )}
                          </td>
                          <td className='text-end'>
                            <Button
                              variant={selected ? 'primary' : 'outline-primary'}
                              size='sm'
                              onClick={(e) => onEditRow(e, r.id)}
                              title='Edit script'
                            >
                              <FaEdit className='me-1' /> Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal
          show={showModal}
          onHide={closeModal}
          size='xl'
          fullscreen='lg-down'
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {form.id ? (
                <>
                  Edit script <code className='ms-1'>{form.script_key || form.id}</code>
                </>
              ) : (
                'New script'
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='pt-2'>
            {loadingScript ? (
              <div className='py-5 text-center'>
                <Spinner animation='border' size='sm' className='me-2' />
                Loading script…
              </div>
            ) : (
              <>
                <Row className='g-3'>
                  <Col lg={5}>
                    <Card className='border h-100'>
                      <Card.Header className='py-2 small fw-semibold'>Preview context</Card.Header>
                      <Card.Body className='small'>
                        <Form.Group className='mb-2'>
                          <Form.Label className='mb-0'>Object API name</Form.Label>
                          <Form.Control
                            size='sm'
                            value={previewObjectName}
                            onChange={(e) => setPreviewObjectName(e.target.value)}
                            placeholder='lead'
                          />
                        </Form.Group>
                        <Form.Group className='mb-2'>
                          <Form.Label className='mb-0'>Preview mode</Form.Label>
                          <Form.Select
                            size='sm'
                            value={previewMode}
                            onChange={(e) => setPreviewMode(e.target.value)}
                          >
                            <option value='api'>API / dynamic form (backend, snippet)</option>
                            <option value='validation'>Validation rule script</option>
                          </Form.Select>
                        </Form.Group>
                        {previewMode === 'api' ? (
                          <>
                            <Form.Group className='mb-2'>
                              <Form.Label className='mb-0'>Record ID (UUID, optional)</Form.Label>
                              <Form.Control
                                size='sm'
                                value={previewRecordId}
                                onChange={(e) => setPreviewRecordId(e.target.value)}
                                placeholder='00000000-0000-0000-0000-000000000000'
                              />
                            </Form.Group>
                            <Form.Group className='mb-0'>
                              <Form.Label className='mb-0'>inputData (JSON)</Form.Label>
                              <Form.Control
                                as='textarea'
                                rows={4}
                                className='font-monospace small'
                                value={previewInputDataText}
                                onChange={(e) => setPreviewInputDataText(e.target.value)}
                              />
                            </Form.Group>
                          </>
                        ) : (
                          <>
                            <Form.Group className='mb-2'>
                              <Form.Label className='mb-0'>triggerKey</Form.Label>
                              <Form.Control
                                size='sm'
                                value={validationTriggerText}
                                onChange={(e) => setValidationTriggerText(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className='mb-2'>
                              <Form.Label className='mb-0'>validation.record (JSON)</Form.Label>
                              <Form.Control
                                as='textarea'
                                rows={5}
                                className='font-monospace small'
                                value={validationRecordText}
                                onChange={(e) => setValidationRecordText(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className='mb-0'>
                              <Form.Label className='mb-0'>previousRecord (JSON, optional)</Form.Label>
                              <Form.Control
                                as='textarea'
                                rows={3}
                                className='font-monospace small'
                                value={validationPrevText}
                                onChange={(e) => setValidationPrevText(e.target.value)}
                              />
                            </Form.Group>
                          </>
                        )}
                        <Button
                          className='mt-3'
                          variant='primary'
                          size='sm'
                          onClick={onPreview}
                          disabled={previewing}
                        >
                          {previewing ? (
                            <Spinner animation='border' size='sm' className='me-1' />
                          ) : (
                            <FaPlay className='me-1' />
                          )}
                          Run preview
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={7}>
                    <Row className='g-2 mb-2'>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>script_key *</Form.Label>
                          <Form.Control
                            size='sm'
                            value={form.script_key}
                            onChange={(e) => setForm((p) => ({ ...p, script_key: e.target.value }))}
                            placeholder='my_unique_script_key'
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>script_type *</Form.Label>
                          <Form.Select
                            size='sm'
                            value={form.script_type}
                            onChange={(e) => setForm((p) => ({ ...p, script_type: e.target.value }))}
                          >
                            {SCRIPT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>display_label</Form.Label>
                          <Form.Control
                            size='sm'
                            value={form.display_label}
                            onChange={(e) => setForm((p) => ({ ...p, display_label: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>target_object</Form.Label>
                          <Form.Control
                            size='sm'
                            value={form.target_object}
                            onChange={(e) => setForm((p) => ({ ...p, target_object: e.target.value }))}
                            placeholder='lead (optional)'
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>description</Form.Label>
                          <Form.Control
                            size='sm'
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>sort_order</Form.Label>
                          <Form.Control
                            size='sm'
                            value={form.sort_order}
                            onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          className='mt-4'
                          type='checkbox'
                          label='Active'
                          checked={!!form.is_active}
                          onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                        />
                      </Col>
                    </Row>

                    <Form.Group className='mb-2'>
                      <Form.Label className='small mb-0'>source_code *</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={12}
                        className='font-monospace small'
                        value={form.source_code}
                        onChange={(e) => setForm((p) => ({ ...p, source_code: e.target.value }))}
                        spellCheck={false}
                      />
                    </Form.Group>

                    <Row className='g-2'>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>config (JSON object)</Form.Label>
                          <Form.Control
                            as='textarea'
                            rows={7}
                            className='font-monospace small'
                            value={form.configText}
                            onChange={(e) => setForm((p) => ({ ...p, configText: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className='small mb-0'>ui_code (JSON, optional)</Form.Label>
                          <Form.Control
                            as='textarea'
                            rows={7}
                            className='font-monospace small'
                            value={form.ui_codeText}
                            onChange={(e) => setForm((p) => ({ ...p, ui_codeText: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {previewResult !== null && (
                  <div className='mt-3'>
                    <div className='small text-muted mb-1'>Preview result</div>
                    <Table bordered size='sm' className='font-monospace small mb-0'>
                      <tbody>
                        <tr>
                          <td style={{ width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {typeof previewResult === 'object'
                              ? JSON.stringify(previewResult, null, 2)
                              : String(previewResult)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className='gap-2'>
            <Button variant='outline-secondary' onClick={closeModal}>
              Cancel
            </Button>
            <Button variant='success' onClick={onSave} disabled={saving || loadingScript}>
              {saving ? (
                <Spinner animation='border' size='sm' className='me-1' />
              ) : (
                <FaSave className='me-1' />
              )}
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        <AppToast
          show={!!toast}
          onClose={() => setToast(null)}
          message={toast?.message}
          type={toast?.type || 'success'}
        />
      </div>
    </div>
  );
}
